import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { dateTime, num } from '../lib/format.js';
import type { Rights } from '../lib/session.js';
import {
  Bar,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  TabBar,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import '../screens-2a.css';

/**
 * Doručitelnost (D-322): odskoky a stížnosti podle domén příjemců, kampaní a odesílatelů,
 * ověření odesílacích domén (SPF, DKIM, DMARC) a ruční seznam reputace u poskytovatelů.
 * Ze SmartEmailingu se jen čte; u každého bloku je vidět zdroj čísel.
 */

interface Bucket {
  key: string;
  label: string;
  sent: number;
  delivered: number;
  softBounced: number;
  hardBounced: number;
  bounced: number;
  bounceRate: number;
  complaints: number | null;
  complaintRate: number | null;
  reasons: Record<string, number>;
}
interface Block {
  source: 'smartemailing' | 'hub';
  buckets: Bucket[];
}
interface Data {
  brands: Array<{ code: string; name: string }>;
  brand: string | null;
  days: number;
  generatedAt: string;
  seError: string | null;
  total: Block;
  domains: Block;
  daily: Block;
  campaigns: Block;
  senders: Block;
  providers: Block;
  rejecting: string[];
  thresholds: Record<string, unknown>;
}
interface Check {
  id: string;
  status: 'ok' | 'warn' | 'error' | 'unknown';
  summary: string;
  explain: string;
  fix: string | null;
  record: string | null;
}
interface DnsData {
  domains: string[];
  report: { checkedAt: string; domains: Array<{ domain: string; checks: Check[] }> } | null;
}
interface ChecklistData {
  items: Array<{
    id: string;
    title: string;
    why: string;
    todo: string;
    url: string;
    linkCheck: 'ok' | 'login' | 'unverified';
    done: boolean;
    at: string | null;
    by: string | null;
  }>;
}

type Tab = 'overview' | 'dns' | 'reputation' | 'limits';
const TABS: Array<{ id: Tab; label: string; hint: string }> = [
  { id: 'overview', label: 'Přehled', hint: 'Odskoky a stížnosti' },
  { id: 'dns', label: 'Autentizace domén', hint: 'SPF, DKIM, DMARC' },
  { id: 'reputation', label: 'Reputace u poskytovatelů', hint: 'Ruční kontrolní seznam' },
  { id: 'limits', label: 'Prahy', hint: 'Kdy se ozve Dohled' },
];

const p1 = (v: number | null): string => (v === null ? '—' : `${(v * 100).toFixed(1)} %`);
const p2 = (v: number | null): string => (v === null ? '—' : `${(v * 100).toFixed(2)} %`);
const SOURCE = { smartemailing: 'zdroj: SmartEmailing', hub: 'zdroj: události hubu' } as const;
const STATUS: Record<Check['status'], { tone: Tone; label: string }> = {
  ok: { tone: 'ok', label: 'V pořádku' },
  warn: { tone: 'warn', label: 'Pozor' },
  error: { tone: 'bad', label: 'Chyba' },
  unknown: { tone: 'neu', label: 'Neověřeno' },
};
const LINK_NOTE = {
  ok: 'odkaz ověřen',
  login: 'vyžaduje přihlášení, obsah neověřen',
  unverified: 'odkaz se nepodařilo ověřit',
} as const;

function Source({ block }: { block: Block }): JSX.Element {
  return <span className="muted">{SOURCE[block.source]}</span>;
}

function RateTable({
  block,
  label,
  rejecting = [],
  testId,
}: {
  block: Block;
  label: string;
  rejecting?: string[];
  testId: string;
}): JSX.Element {
  if (block.buckets.length === 0)
    return <EmptyState title="Žádná data">Za zvolené období nejsou odeslané zprávy.</EmptyState>;
  const max = Math.max(...block.buckets.map((b) => b.bounceRate), 0.0001);
  return (
    <table data-testid={testId}>
      <thead>
        <tr>
          <th>{label}</th>
          <th>Odesláno</th>
          <th>Doručeno</th>
          <th>Odskoky</th>
          <th>Podíl</th>
          <th>Stížnosti</th>
          <th>Důvody odskoků</th>
        </tr>
      </thead>
      <tbody>
        {block.buckets.map((b) => (
          <tr key={b.key}>
            <td>
              {b.label}
              {rejecting.includes(b.key) ? (
                <>
                  {' '}
                  <StatusBadge tone="bad">odmítá</StatusBadge>
                </>
              ) : null}
            </td>
            <td>{num(b.sent)}</td>
            <td>{num(b.delivered)}</td>
            <td>
              {num(b.bounced)} <span className="muted">(tvrdé {num(b.hardBounced)})</span>
            </td>
            <td>
              {p1(b.bounceRate)} <Bar value={b.bounceRate} max={max} />
            </td>
            <td>{b.complaints === null ? '—' : `${num(b.complaints)} (${p2(b.complaintRate)})`}</td>
            <td className="muted">
              {Object.entries(b.reasons)
                .sort((x, y) => y[1] - x[1])
                .slice(0, 3)
                .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`)
                .join(', ') || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Overview({ rights }: { rights: Rights }): JSX.Element {
  const [days, setDays] = useState(30);
  const [brand, setBrand] = useState('');
  const client = useQueryClient();
  const q = useQuery({
    queryKey: ['deliverability', days, brand],
    queryFn: () => api.get<Data>(`/api/deliverability${qs({ days, brand })}`),
  });
  const refresh = useMutation({
    mutationFn: () => api.post('/api/deliverability/refresh'),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['deliverability'] }),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const t = d.total.buckets[0];
  return (
    <>
      <div className="toolbar">
        <select
          aria-label="Značka"
          value={brand || d.brand || ''}
          onChange={(e) => setBrand(e.target.value)}
        >
          {d.brands.map((b) => (
            <option key={b.code} value={b.code}>
              {b.name}
            </option>
          ))}
        </select>
        {[7, 30, 90].map((n) => (
          <button
            key={n}
            type="button"
            className={n === days ? 'btn primary' : 'btn'}
            onClick={() => setDays(n)}
          >
            {n} dní
          </button>
        ))}
        {rights.write('deliverability') ? (
          <button type="button" className="btn" onClick={() => refresh.mutate()}>
            Načíst znovu ze SmartEmailingu
          </button>
        ) : null}
        <span className="muted">Stav k {dateTime(d.generatedAt)}</span>
      </div>
      {d.seError ? (
        <div className="confirm dngc" role="alert" data-testid="deliverability-se-error">
          <b>{d.seError}</b> Níže jsou jen čísla, která hub spočítal z uložených událostí.
        </div>
      ) : null}
      <Panel title="Celkem" subtitle={<Source block={d.total} />}>
        {t ? (
          <p data-testid="deliverability-total">
            Odesláno <b>{num(t.sent)}</b>, doručeno <b>{num(t.delivered)}</b>, odskoky{' '}
            <b>{num(t.bounced)}</b> ({p1(t.bounceRate)}), stížnosti na spam{' '}
            <b>
              {t.complaints === null
                ? 'SmartEmailing nevrací'
                : `${num(t.complaints)} (${p2(t.complaintRate)})`}
            </b>
          </p>
        ) : (
          <EmptyState title="Žádná data">Za zvolené období nejsou odeslané zprávy.</EmptyState>
        )}
      </Panel>
      <Panel title="Podle poskytovatele příjemců" subtitle={<Source block={d.providers} />}>
        <RateTable block={d.providers} label="Poskytovatel" testId="deliverability-providers" />
      </Panel>
      <Panel title="Podle domény příjemců" subtitle={<Source block={d.domains} />}>
        <RateTable
          block={{ ...d.domains, buckets: d.domains.buckets.slice(0, 25) }}
          label="Doména"
          rejecting={d.rejecting}
          testId="deliverability-domains"
        />
      </Panel>
      <Panel title="Denní vývoj" subtitle={<Source block={d.daily} />}>
        <RateTable block={d.daily} label="Den" testId="deliverability-daily" />
      </Panel>
      <Panel title="Podle odesílatele nebo značky" subtitle={<Source block={d.senders} />}>
        <RateTable block={d.senders} label="Odesílatel" testId="deliverability-senders" />
      </Panel>
      <Panel
        title="Podle kampaně nebo rozesílky (BC&lt;id&gt;)"
        subtitle={<Source block={d.campaigns} />}
      >
        <RateTable block={d.campaigns} label="Kampaň" testId="deliverability-campaigns" />
      </Panel>
    </>
  );
}

function Dns({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: ['deliverability-dns'],
    queryFn: () => api.get<DnsData>('/api/deliverability/dns'),
  });
  const run = useMutation({
    mutationFn: () => api.post('/api/deliverability/dns/check'),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['deliverability-dns'] }),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { report, domains } = q.data;
  return (
    <Panel
      title="Autentizace domén"
      subtitle={
        report
          ? `Poslední kontrola ${dateTime(report.checkedAt)}`
          : 'Zatím neproběhla žádná kontrola'
      }
      action={
        rights.write('deliverability') ? (
          <button
            type="button"
            className="btn primary"
            onClick={() => run.mutate()}
            disabled={run.isPending}
          >
            {run.isPending ? 'Kontroluji…' : 'Zkontrolovat teď'}
          </button>
        ) : undefined
      }
    >
      <p className="muted">
        Kontrolované domény: {domains.join(', ')}. Další domény přidáte na záložce Prahy. Hub jen
        čte DNS, nic nemění.
      </p>
      {run.error ? <ErrorState error={run.error} /> : null}
      {!report ? (
        <EmptyState title="Bez výsledku">
          Spusťte Zkontrolovat teď, nebo počkejte na denní kontrolu.
        </EmptyState>
      ) : (
        report.domains.map((dom) => (
          <div key={dom.domain} data-testid={`dns-${dom.domain}`}>
            <h3>{dom.domain}</h3>
            <table>
              <tbody>
                {dom.checks.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id.toUpperCase()}</td>
                    <td>
                      <StatusBadge tone={STATUS[c.status].tone}>
                        {STATUS[c.status].label}
                      </StatusBadge>
                    </td>
                    <td>
                      {c.summary}
                      {c.status === 'ok' || c.status === 'unknown' ? null : (
                        <div className="muted">{c.explain}</div>
                      )}
                      {c.fix ? <div>Jak opravit: {c.fix}</div> : null}
                      {c.record ? <code className="muted">{c.record}</code> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </Panel>
  );
}

function Reputation({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: ['deliverability-checklist'],
    queryFn: () => api.get<ChecklistData>('/api/deliverability/checklist'),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; done: boolean }) =>
      api.put<ChecklistData>(`/api/deliverability/checklist/${v.id}`, { done: v.done }),
    onSuccess: (data) => client.setQueryData(['deliverability-checklist'], data),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <Panel
      title="Reputace u poskytovatelů"
      subtitle="Hub tyto služby nevolá; odškrtněte, až to máte nastavené. Zapíše se kdo a kdy."
    >
      <ul className="checklist" data-testid="deliverability-checklist">
        {q.data.items.map((i) => (
          <li key={i.id}>
            <label>
              <input
                type="checkbox"
                checked={i.done}
                disabled={!rights.write('deliverability') || toggle.isPending}
                onChange={(e) => toggle.mutate({ id: i.id, done: e.target.checked })}
              />{' '}
              <b>{i.title}</b>
            </label>
            <div>{i.why}</div>
            <div>Co udělat: {i.todo}</div>
            <div className="muted">
              <a href={i.url} target="_blank" rel="noopener noreferrer">
                {i.url}
              </a>{' '}
              ({LINK_NOTE[i.linkCheck]})
              {i.done && i.at ? ` · odškrtnuto ${dateTime(i.at)}${i.by ? `, ${i.by}` : ''}` : ''}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

const NUM_FIELDS: Array<{ key: string; label: string; pct?: boolean }> = [
  {
    key: 'deliverability_domain_reject_rate',
    label: 'Doména odmítá od podílu odskoků (0,1 = 10 %)',
  },
  { key: 'deliverability_complaint_rate', label: 'Stížnosti na spam od podílu (0,001 = 0,1 %)' },
  { key: 'deliverability_min_sent', label: 'Nejméně odeslaných pro vyhodnocení' },
  { key: 'deliverability_window_days', label: 'Okno pravidel (dní)' },
  { key: 'dns_spf_max_lookups', label: 'Nejvíc DNS dotazů SPF' },
];
const BOOL_FIELDS = [
  { key: 'dns_rule_spf', label: 'Hlídat SPF' },
  { key: 'dns_rule_dkim', label: 'Hlídat DKIM' },
  { key: 'dns_rule_dmarc', label: 'Hlídat DMARC' },
];

function Limits({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: ['deliverability', 30, ''],
    queryFn: () => api.get<Data>(`/api/deliverability${qs({ days: 30 })}`),
  });
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const save = useMutation({
    mutationFn: () => api.put('/api/deliverability/settings', draft),
    onSuccess: () => {
      setDraft({});
      void client.invalidateQueries({ queryKey: ['deliverability'] });
    },
  });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const th = { ...q.data.thresholds, ...draft };
  const set = (k: string, v: unknown): void => setDraft((s) => ({ ...s, [k]: v }));
  const canWrite = rights.write('deliverability');
  const list = (k: string): string => ((th[k] as string[] | undefined) ?? []).join(', ');
  const toList = (s: string): string[] =>
    s
      .split(/[,\s]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  return (
    <Panel title="Prahy a kontroly DNS" subtitle="Pravidla Dohledu čtou tyto hodnoty.">
      <div className="form-grid">
        {NUM_FIELDS.map((f) => (
          <label key={f.key} className="field">
            <span>{f.label}</span>
            <input
              type="number"
              step="any"
              disabled={!canWrite}
              value={String(th[f.key] ?? '')}
              onChange={(e) => set(f.key, Number(e.target.value))}
            />
          </label>
        ))}
        {BOOL_FIELDS.map((f) => (
          <label key={f.key}>
            <input
              type="checkbox"
              disabled={!canWrite}
              checked={th[f.key] === true}
              onChange={(e) => set(f.key, e.target.checked)}
            />{' '}
            {f.label}
          </label>
        ))}
        <label className="field">
          <span>Nejslabší přijatelná politika DMARC</span>
          <select
            disabled={!canWrite}
            value={String(th['dns_dmarc_min_policy'])}
            onChange={(e) => set('dns_dmarc_min_policy', e.target.value)}
          >
            <option value="none">none</option>
            <option value="quarantine">quarantine</option>
            <option value="reject">reject</option>
          </select>
        </label>
        <label className="field">
          <span>Selektory DKIM (oddělené čárkou; názvy jsou v účtu SmartEmailingu)</span>
          <input
            disabled={!canWrite}
            value={list('dns_dkim_selectors')}
            onChange={(e) => set('dns_dkim_selectors', toList(e.target.value))}
          />
        </label>
        <label className="field">
          <span>Požadovaný include v SPF (např. include:… od SmartEmailingu)</span>
          <input
            disabled={!canWrite}
            value={String(th['dns_spf_include'] ?? '')}
            onChange={(e) => set('dns_spf_include', e.target.value.trim())}
          />
        </label>
        <label className="field">
          <span>Další odesílací domény (oddělené čárkou)</span>
          <input
            disabled={!canWrite}
            value={list('dns_extra_domains')}
            onChange={(e) => set('dns_extra_domains', toList(e.target.value))}
          />
        </label>
      </div>
      {save.error ? <ErrorState error={save.error} /> : null}
      {canWrite ? (
        <button
          type="button"
          className="btn primary"
          disabled={Object.keys(draft).length === 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          Uložit
        </button>
      ) : null}
    </Panel>
  );
}

export function Deliverability({ rights }: { rights: Rights }): JSX.Element {
  const [tab, setTab] = useState<Tab>('overview');
  return (
    <div data-testid="deliverability">
      <PageHeader
        title="Doručitelnost"
        description="Jestli zprávy dorazí: odskoky a stížnosti, ověření domén a reputace u poskytovatelů."
      />
      <TabBar tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'overview' ? <Overview rights={rights} /> : null}
      {tab === 'dns' ? <Dns rights={rights} /> : null}
      {tab === 'reputation' ? <Reputation rights={rights} /> : null}
      {tab === 'limits' ? <Limits rights={rights} /> : null}
    </div>
  );
}
